/**
 * Umpire Appointments — the supervision surface as one screen (module 02, Part B).
 *
 * This component owns both datasets and the four writes that may touch one of them. Everything
 * below it draws.
 *
 * Five rules it exists to keep:
 *
 *  1. **The two halves never cross** (Part C invariant 5). They arrive as `tmsActual` and
 *     `aiReference` on every fixture, they are filtered separately, they are counted separately
 *     and they are drawn by separate panel instances told which half they are. No array here
 *     ever holds them mixed, and the only place they meet is §B9's evenness note, which prints
 *     two measurements beside each other rather than merging them.
 *
 *  2. **The TMS half is read-only by construction, not by policy.** No write on this screen
 *     names it, no handler is passed to its fixture list, and the API refuses one anyway.
 *
 *  3. **The server's reply is authoritative.** Draft, clear, fill and edit all answer with both
 *     halves recomputed (Part C invariant 1). An edit is optimistic only so the cell moves at
 *     once, and a refusal puts it back exactly as it was — the three derived AI tables with it.
 *
 *  4. **The TMS half answers only to `Load Appointments`** (Part C's states table: *"Fetch not
 *     yet run — panels show `Not Fetched This Session`"*, which RUN 81 narrowed to this half).
 *     §B4 puts the button on the panel, and a TMS half that had filled itself on the way in
 *     would make that button a lie about when the data on screen was true. Every TMS figure
 *     here is therefore drawn from `tmsFixtures`, which stays empty until the press, and the
 *     stamp is set by the press and by nothing else — a draft press no longer sets it.
 *
 *     **Decision #13 (the TD, 16 September 2026; §B4a) — this half says what it is.** The press
 *     has never read TMS: `GET /umpire-appointments` answers from this system's own record. So
 *     the button is `Load Appointments`, the half is `As Recorded In This System`, the stamp says
 *     `Not A Live TMS Read`, and the state before the press is `Not Loaded This Session`. The old
 *     words — `Fetch TMS Actual`, `TMS appointments refreshed` — were read on 14 September as a
 *     live TMS read, which is precisely what they were not.
 *
 *     **The AI half opens with the stored draft** (§Panel 5a, RUN 81). The panel reads this
 *     system's own record once as it mounts and draws the advisory half of the answer at once,
 *     because a page that hid a stored draft until the TMS press was telling the TD to press
 *     `Draft The Pool Stage` over the top of a draft he could not see. Every write on that half
 *     waits until the read has answered.
 *
 *  5. **Every panel filters itself** (Part C invariant 2). Seven panels, seven independent tab
 *     bars, and the two fixture lists keep their own pager as well. RUN 81 gives each matrix a
 *     second control of its own, `On Field Only`, on the same terms.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import type { UmpireSlot, TmsMatchDbId } from "@hockeytd/shared";

import {
  ApiError,
  deleteUmpireSlot,
  fetchUmpireAppointments,
  patchUmpireSlot,
  postUmpireClearDrafts,
  postUmpireDraft,
  postUmpireSlot,
  type UmpireScope,
} from "../../api/client.js";
import type {
  OfficialView,
  UmpireAppointmentsViewBody,
  UmpireClearResponseBody,
  UmpireDraftResponseBody,
  UmpireDraftScope,
  UmpireFixtureBody,
  UmpireSlotBody,
} from "../../api/types.js";
import { Skeleton } from "../../app/states.js";
import { useTournament } from "../../app/session.js";
import { competitionLabel, type CompetitionFilter, type StageFilter } from "../appointments/derive.js";
import { countOf } from "../appointments/vocabulary.js";
import { landingDay, tournamentToday } from "../schedule/matchDays.js";
import { useCellFlash } from "../appointments/useCellFlash.js";
import { exportWorkbook, type WorkbookExporter } from "../export/render.js";
import {
  scopeLine,
  umpireAiFixtureRows,
  umpireMatrixRows,
  umpireTmsFixtureRows,
} from "../export/rows.js";
import {
  buildUmpireAiWorkbook,
  buildUmpireTmsWorkbook,
  exportFilename,
  exportNotice,
} from "../export/workbook.js";
import { EvennessNote } from "./EvennessNote.js";
import {
  SHOW_STORED_DRAFT_ID,
  UmpireFixtureListPanel,
  umpireRowKey,
} from "./UmpireFixtureListPanel.js";
import { UmpireMatrixPanel } from "./UmpireMatrixPanel.js";
import { UmpirePicker } from "./UmpirePicker.js";
import { UmpireSummaryPanel } from "./UmpireSummaryPanel.js";
import { UmpireTeamsPanel } from "./UmpireTeamsPanel.js";
import {
  buildUmpireMatrix,
  buildUmpireSummary,
  buildUmpireTeams,
  filterByGender,
  filterUmpireFixtures,
  umpireDerivedCellValues,
  umpirePanelMembers,
  type UmpireGenderFilter,
} from "./derive.js";

/**
 * RUN 38 — what the two draft presses are called, in the AI half's own words.
 *
 * Named once and read by the button, its confirmation and its notice, so that a press cannot
 * be labelled one thing on the head, described as another in the dialog and reported as a
 * third in the sentence underneath.
 */
export const UMPIRE_DRAFT_LABEL: Readonly<Record<UmpireDraftScope, string>> = {
  pool: "Draft The Pool Stage",
  all: "Draft All",
};

/**
 * What one press of the advisory draft reached, in one sentence.
 *
 * Modelled on the TO page's stage-draft notice and deliberately so: days first, then matches,
 * then the slots actually written, and then the standing truth about this half. A Technical
 * Delegate moving between the two pages should not have to learn two ways of reading the same
 * kind of sentence.
 *
 * Every number is the response's own — `scopedDays` and `scopedFixtures` are what the route
 * could fill, `drafted` is what it actually wrote, and the gap between them is the cells the
 * TD had already adjusted by hand, which a draft keeps.
 */
export function umpireDraftNote(drafted: UmpireDraftResponseBody, variant = 0): string {
  // `countOf` is the TO page's pluraliser, shared rather than copied: `match` takes `es`, and
  // the `60 matchs` that reached the screen once came from a sentence exactly like this one
  // written with its own local rule. One function, both pages.
  //
  // RUN 40 — one sentence for both presses, with the variant on the front when there is one,
  // so a first draft and a re-draft can never report the same press two different ways. At
  // variant 0 it is byte-for-byte the sentence RUN 38 shipped.
  //
  // `drafted` is the ONLY figure the response carries about what moved, and it is the count of
  // advisory cells this press wrote. Cells the TD adjusted by hand are kept and are deliberately
  // NOT among them, which is why the word is `re-drafted` rather than `changed`: the response
  // cannot say how many of those cells came back with a different name on them, and a sentence
  // that implied it could would be inventing a figure.
  return (
    (variant === 0
      ? `${UMPIRE_DRAFT_LABEL[drafted.draftScope]} · `
      : `Re-draft · variant ${String(variant)} · ${UMPIRE_DRAFT_LABEL[drafted.draftScope]} · `) +
    `${countOf(drafted.scopedDays, "day")} · ` +
    `${countOf(drafted.scopedFixtures, "match")} · ` +
    `${countOf(drafted.drafted, "advisory slot")} ${variant === 0 ? "drafted" : "re-drafted"} ` +
    `as an evenness reference. ` +
    `Nothing pushed — the Umpires Managers own umpire appointments.`
  );
}

/**
 * The four writes, behind one seam. The real implementations are the API client's; the
 * indirection exists so a component test can drive the panel without a network.
 *
 * **There is no write against the Umpires Managers' half, and there is nowhere to put one.**
 */
export interface UmpireWriter {
  fetch: (scope: UmpireScope) => Promise<UmpireAppointmentsViewBody>;
  /**
   * RUN 38 — one route, two presses. `draftScope` is `"pool"` for `Draft The Pool Stage` and
   * `"all"` for `Draft All`; omitted, the route's own default of `pool` applies.
   *
   * RUN 40 — `rotationOffset` is `Re-Draft`: the engine's variant number, which produces a
   * different but equally rule-abiding arrangement of everything the engine chose. Omitted by
   * both plain draft presses, which are variant 0.
   */
  draft: (
    scope: UmpireScope,
    draftScope?: UmpireDraftScope,
    rotationOffset?: number,
  ) => Promise<UmpireDraftResponseBody>;
  clearDrafts: (scope: UmpireScope) => Promise<UmpireClearResponseBody>;
  create: (body: {
    tmsMatchDbId: TmsMatchDbId;
    slot: UmpireSlot;
    tmsPersonId: string;
  }) => Promise<UmpireAppointmentsViewBody>;
  patch: (appointmentId: string, body: { tmsPersonId: string }) => Promise<UmpireAppointmentsViewBody>;
  withdraw: (appointmentId: string) => Promise<UmpireAppointmentsViewBody>;
}

export const LIVE_UMPIRE_WRITER: UmpireWriter = {
  fetch: fetchUmpireAppointments,
  draft: postUmpireDraft,
  clearDrafts: postUmpireClearDrafts,
  create: postUmpireSlot,
  patch: patchUmpireSlot,
  withdraw: deleteUmpireSlot,
};

export interface UmpireAppointmentsPanelProps {
  /** The roster, for the picker and for the panel rows. Empty when it could not be loaded. */
  officials: readonly OfficialView[];
  /** Matches the schedule reports as played — §B8's `completed` half, measured not inferred. */
  completedMatchIds?: ReadonlySet<number>;
  /** False where the schedule could not be read at all, so the summary can say so. */
  scheduleLoaded?: boolean;
  /** A view already in hand — a test's seed, and nothing else uses it. */
  initialView?: UmpireAppointmentsViewBody | null;
  writer?: UmpireWriter;
  /** §B11's two exports, behind one seam. The default loads `exceljs` on the press. */
  exporter?: WorkbookExporter;
  /**
   * RUN 37 — the instant "today" is reckoned from, for where the two lists open.
   *
   * A defaulted parameter rather than a call to the clock buried in the logic, matching how
   * the rest of this build makes time testable.
   */
  now?: Date;
  /**
   * RUN 81A — how long the opening read may take before the page says so and offers
   * `Show Stored Draft`. Defaulted, and injectable so a test can reach the branch in milliseconds.
   */
  storedReadTimeoutMs?: number;
}

/** RUN 81A — the opening read's limit: a stalled read on venue wifi must not leave the page asleep. */
export const STORED_READ_TIMEOUT_MS = 20_000;

/** RUN 81A — the opening read took longer than {@link STORED_READ_TIMEOUT_MS}. */
class StoredReadTimeout extends Error {
  public constructor() {
    super(
      "The stored draft did not arrive in time. Nothing on this page has changed — press " +
        "Show Stored Draft to read it again.",
    );
  }
}

/** Settle with `promise`, or reject with {@link StoredReadTimeout} after `ms`, whichever is first. */
function withinTime<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new StoredReadTimeout());
    }, ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error instanceof Error ? error : new Error(String(error)));
      },
    );
  });
}

type Pending =
  /**
   * RUN 81 — the read of the stored record as the page opens (§Panel 5a). A kind of its own so
   * it can make the page busy — no press may run over a draft nobody has seen yet — without
   * lighting `Fetching…` on a button nobody pressed.
   */
  | { kind: "load" }
  | { kind: "fetch" }
  /** RUN 38 — the two draft presses share one pending kind; `draftScope` says which. */
  | { kind: "draft"; scope: UmpireDraftScope }
  /** RUN 40 — `Re-Draft` is its own kind, so its spinner cannot appear on a draft button. */
  | { kind: "redraft" }
  | { kind: "clear" }
  | { kind: "slot"; row: string }
  | null;

interface PickerState {
  fixture: UmpireFixtureBody;
  slot: UmpireSlotBody;
  anchor: HTMLElement;
}

/**
 * RUN 82Q (final QA, 18 September 2026) — where keyboard focus goes once the render a question's
 * answer caused has landed (WCAG 2.4.3).
 *
 * Held as state and acted on in an effect rather than moved on the spot: what an answer focuses
 * does not exist yet when the answer is given — the notice and the failure strip are drawn by the
 * render the write causes — and the press that asked is gone by then. Every request is a fresh
 * object, so asking twice focuses twice.
 *
 * `opener` is the press that opened the question, kept in a ref rather than named by a DOM id:
 * the four presses live on `UmpireFixtureListPanel` and carry none, and this run may not add them
 * there. See the TO page's `FocusRequest`, which can name its own by id.
 */
type FocusRequest = { kind: "opener" } | { kind: "note" } | { kind: "failure" };

/* ── RUN 82Q (final QA, 18 September 2026) — the three questions are modal ──────────────────
 *
 * The final QA found this page's three questions — the draft pair's, `Re-Draft`'s and `Clear
 * Drafts`' — drawn as `role="alertdialog"` with no `aria-modal`, no focus move when they open and
 * no tab containment. `Draft All` left the focus on `Draft All`, and five Tabs then walked behind
 * the open question (`Draft The Pool Stage` → `Re-Draft` → `Clear Drafts` → `All` → `Men's`), so
 * a keyboard TD could start a second write with the question still open — over the stored draft
 * that question exists to protect.
 *
 * What is built here is the TO page's answer to the same finding (module 03, RUN 82A/82D/82G/82I),
 * brought across rather than invented, so a Technical Delegate moving between the two pages meets
 * one behaviour: `aria-modal` with the question's own body as its description, the workbench
 * `inert`, the focus into `Cancel` — the answer that changes nothing — Tab kept inside from within
 * and from without, a transparent layer taking the clicks on the page behind, no Escape, and no
 * second question over an open one.
 *
 * **Mirrored, not shared.** `questionTabStops`, `isOnStop`, `keepTabInside` and `describedBy` are
 * file-local to `AppointmentsPanel.tsx`, and this run may not touch `features/appointments`:
 * module 03 is live, and exporting from it to reach these would put a change into the TO page on
 * the morning of Day 1 for the sake of module 02. One exported home for both pages is the right
 * end state and is a job of its own.
 */

/**
 * The places a Tab can land inside a question, in order.
 *
 * A group of radios is ONE stop, reached at its checked radio (or its first, where none is
 * checked), exactly as the browser tabs through it — `Re-Draft` asks its scope that way. A
 * disabled control is no stop at all.
 */
function questionTabStops(question: HTMLElement): HTMLElement[] {
  const stops: HTMLElement[] = [];
  const groups = new Set<string>();
  for (const element of question.querySelectorAll<HTMLButtonElement | HTMLInputElement>(
    "button, input",
  )) {
    if (element.disabled) continue;
    if (element instanceof HTMLInputElement && element.type === "radio" && element.name !== "") {
      if (groups.has(element.name)) continue;
      groups.add(element.name);
      const group = [...question.querySelectorAll<HTMLInputElement>('input[type="radio"]')].filter(
        (radio) => radio.name === element.name && !radio.disabled,
      );
      stops.push(group.find((radio) => radio.checked) ?? element);
      continue;
    }
    stops.push(element);
  }
  return stops;
}

/** Whether the focus is on this stop — for a radio group, on any radio of it. */
function isOnStop(active: Element | null, stop: HTMLElement): boolean {
  if (active === stop) return true;
  return (
    active instanceof HTMLInputElement &&
    stop instanceof HTMLInputElement &&
    active.type === "radio" &&
    stop.type === "radio" &&
    active.name !== "" &&
    active.name === stop.name
  );
}

/**
 * Tab and Shift+Tab kept on a question's own stops, which is what its `aria-modal` promises a
 * keyboard.
 *
 * `inert` takes the workbench behind the question out of the tab order; what it leaves is the rest
 * of the page — the masthead, How To Use, the module nav — which a Tab from the question's last
 * stop would still reach. So the last and first stops hand the focus to each other and every step
 * between them is the browser's own. Only a stop that can take the focus counts: while a write is
 * out the answer is disabled, and Tab then stays on `Cancel`. There is deliberately no Escape —
 * the frame's only ways out are its two answers.
 */
function keepTabInside(event: React.KeyboardEvent<HTMLElement>): void {
  if (event.key !== "Tab") return;
  const stops = questionTabStops(event.currentTarget);
  const first = stops[0];
  const last = stops[stops.length - 1];
  if (first === undefined || last === undefined) return;
  const active = document.activeElement;
  const wrapTo = event.shiftKey
    ? isOnStop(active, first)
      ? last
      : null
    : isOnStop(active, last)
      ? first
      : null;
  if (wrapTo === null) return;
  event.preventDefault();
  wrapTo.focus();
}

/** A question's `aria-describedby`, from the ids of the paragraphs it is drawing now. */
function describedBy(ids: readonly (string | null)[]): string {
  return ids.filter((id): id is string => id !== null).join(" ");
}

/**
 * `Loaded 08:40 JST · This System's Record, Not A Live TMS Read` — §B2's stamp, on the
 * tournament's own clock.
 *
 * Decision #13 (§B4a): it read `TMS Actual Fetched 08:40 JST Today`, and nothing behind the press
 * has ever read TMS. The stamp is the one line on the banner saying what the figures under it
 * are, so it says where they came from as well as when.
 */
export function fetchStampText(at: Date | null): string {
  if (at === null) return "Not Loaded This Session";
  const time = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Tokyo",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(at);
  return `Loaded ${time} JST · This System's Record, Not A Live TMS Read`;
}

/**
 * RUN 81 — what the TMS half draws before `Load Appointments` is pressed: one empty array, made
 * once, so the memos that read it are not recomputed on every draw.
 */
const NOTHING_LOADED: readonly UmpireFixtureBody[] = [];

/** The optimistic cell — the picked umpire in place, pending the server's own answer. */
function withOptimisticSlot(
  view: UmpireAppointmentsViewBody,
  tmsMatchDbId: TmsMatchDbId,
  target: UmpireSlot,
  umpire: OfficialView,
): UmpireAppointmentsViewBody {
  return {
    ...view,
    fixtures: view.fixtures.map((fixture) => ({
      ...fixture,
      // The Umpires Managers' half is copied through untouched, by name. An optimistic write
      // that used a computed key could reach it; this cannot.
      tmsActual: fixture.tmsActual,
      aiReference: fixture.aiReference.map((slot) =>
        fixture.tmsMatchDbId === tmsMatchDbId && slot.slot === target
          ? {
              ...slot,
              tmsPersonId: umpire.tmsPersonId,
              mediaName: umpire.mediaName,
              country: umpire.country,
              panel: umpire.panel,
              status: "td_modified" as const,
            }
          : slot,
      ),
    })),
  };
}

export function UmpireAppointmentsPanel({
  officials,
  completedMatchIds = new Set<number>(),
  scheduleLoaded = true,
  initialView = null,
  writer = LIVE_UMPIRE_WRITER,
  exporter = exportWorkbook,
  now,
  storedReadTimeoutMs = STORED_READ_TIMEOUT_MS,
}: UmpireAppointmentsPanelProps): React.JSX.Element {
  const tournament = useTournament();
  const [view, setView] = useState<UmpireAppointmentsViewBody | null>(initialView);
  const [fetchedAt, setFetchedAt] = useState<Date | null>(initialView === null ? null : new Date());
  const [pending, setPending] = useState<Pending>(null);
  const [note, setNote] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [slotError, setSlotError] = useState<{ row: string; message: string } | null>(null);
  const [picker, setPicker] = useState<PickerState | null>(null);
  const [clearPrompt, setClearPrompt] = useState(false);
  /**
   * RUN 38 — which draft the TD has asked for and not yet confirmed, or null.
   *
   * Both presses ask first, which the single `Draft All` never did. That is not caution for
   * its own sake: there are now TWO of them, they differ only in how far they reach, and the
   * one thing a page with two similar buttons owes its user is a sentence naming which
   * matches the press in front of him is about to touch.
   */
  const [draftPrompt, setDraftPrompt] = useState<UmpireDraftScope | null>(null);
  /** RUN 40 — `Re-Draft` asks the same kind of question the two draft presses do. */
  const [redraftPrompt, setRedraftPrompt] = useState(false);
  /**
   * How far the re-draft reaches, chosen in its own dialog.
   *
   * The two draft buttons make that choice by which one the hand landed on; `Re-Draft` is one
   * button, so it asks — and it offers exactly the pair they offer, defaulting to the pool
   * stage because that is the narrower press and the one the page's own default draft is.
   */
  const [redraftScope, setRedraftScope] = useState<UmpireDraftScope>("pool");
  /**
   * RUN 40 — which shuffle of the advisory draft the page is on.
   *
   * 0 is a plain `Draft The Pool Stage` or `Draft All`, which send no offset at all. Each
   * `Re-Draft` press sends `variant + 1` and banks the counter only on the way back, so a
   * press that fails leaves it where it was and the next press is the same variant rather than
   * a skipped one.
   *
   * **It is page-session state and a reload starts again at variant 0**, exactly as the TO
   * page's counter does. That is the behaviour wanted rather than a limitation worked around:
   * the engine is deterministic in the offset, so a re-draft after a reload reproduces variant
   * 1 exactly — the same draft, not a new one. The counter only says how many shuffles produced
   * what is on screen in this sitting; what is in the database is the draft.
   */
  const [variant, setVariant] = useState(0);
  /** Which of the two exports is running, so one spinner cannot appear on both buttons. */
  const [exportingHalf, setExportingHalf] = useState<"tmsActual" | "aiReference" | null>(null);
  /**
   * RUN 81 — why the stored record could not be read as the page opened, or null (§Panel 5a).
   *
   * Kept apart from `failure`, which reports presses: this one decides what the AI list's empty
   * state says and whether it offers `Show Stored Draft`, and a press that fails later must not be
   * mistaken for it.
   */
  const [loadFailure, setLoadFailure] = useState<string | null>(null);
  /** RUN 81B — how many times the stored read has failed, which re-keys the failure's alert. */
  const [loadAttempts, setLoadAttempts] = useState(0);
  /** RUN 81 — false once the panel has gone, so a late answer to the opening read sets nothing. */
  const mounted = useRef(true);
  /**
   * RUN 81A — the opening read has been started, once, for the life of the panel. StrictMode runs
   * the mount effect twice in development, and a second read would race the first.
   */
  const opened = useRef(false);
  /** RUN 81A — a read of the stored record is in flight, so `Show Stored Draft` cannot stack a second. */
  const loadInFlight = useRef(false);
  /** RUN 82Q — the hand-off the last answer or `Cancel` asked for. See `FocusRequest`. */
  const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);
  /**
   * RUN 82Q — the press that opened the question that is open, so `Cancel` can hand the focus
   * back to it.
   *
   * Read off `document.activeElement` as the question opens, because the four presses are drawn by
   * `UmpireFixtureListPanel` and carry no id this panel could name. A keyboard press is always the
   * focused element, which is the user this finding is about; a pointer press that leaves the
   * focus elsewhere (Safari does not focus a button on click) hands back nothing, and the focus
   * then stays where the TD left it, exactly as it did before this run.
   */
  const opener = useRef<HTMLElement | null>(null);
  const noteRef = useRef<HTMLParagraphElement | null>(null);
  const failureRef = useRef<HTMLDivElement | null>(null);
  /**
   * RUN 82Q — every question this panel draws, and nothing else on the page. The focus that goes
   * into an opening question is looked for HERE rather than across the document: the masthead
   * draws its own `alertdialog` for `Sign Out`, before this page in document order, and a search
   * of the whole document would hand a draft question's focus to `Sign Out Anyway`.
   */
  const questionsRef = useRef<HTMLDivElement | null>(null);

  /* Seven panels, seven filters (Part C invariant 2). RUN 37 took the two pagers away: both
     fixture lists are now one scrolling table each, opened at today. */
  const [tmsFixComp, setTmsFixComp] = useState<CompetitionFilter>("all");
  const [tmsFixStage, setTmsFixStage] = useState<StageFilter>("all");
  const [tmsMatComp, setTmsMatComp] = useState<CompetitionFilter>("all");
  const [tmsTcComp, setTmsTcComp] = useState<CompetitionFilter>("all");
  const [sumGender, setSumGender] = useState<UmpireGenderFilter>("all");
  const [aiFixComp, setAiFixComp] = useState<CompetitionFilter>("all");
  const [aiFixStage, setAiFixStage] = useState<StageFilter>("all");
  const [aiMatComp, setAiMatComp] = useState<CompetitionFilter>("all");
  const [aiTcComp, setAiTcComp] = useState<CompetitionFilter>("all");
  /* RUN 81 — §B6a's `On Field Only`, one per matrix and off on every open. Two states and not
     one, on the same terms as the competition tabs beside them: pressing it on the advisory
     matrix must not quietly re-count the Umpires Managers' matrix above it. */
  const [tmsMatOnField, setTmsMatOnField] = useState(false);
  const [aiMatOnField, setAiMatOnField] = useState(false);

  const fixtures: readonly UmpireFixtureBody[] = view?.fixtures ?? [];
  /**
   * RUN 81 — the fixtures the TMS half may draw: every fixture once `Load Appointments` has been
   * pressed this session, and none before it.
   *
   * The stored record read as the page opens carries both halves, because the route answers
   * both from one moment. Its advisory half is shown straight away; its TMS half is not, because
   * the TMS half answers to its own button (rule 4 above). So every TMS list, table, count and
   * export on this page reads this array rather than `fixtures` — one gate, in one place,
   * rather than a `fetchedAt` check that each of them would have to remember.
   */
  const tmsFixtures: readonly UmpireFixtureBody[] = fetchedAt === null ? NOTHING_LOADED : fixtures;

  /**
   * RUN 37 — the day both lists open at, on the tournament's own clock.
   *
   * One value for both halves. They are the same schedule read two ways, and a page that
   * opened `TMS Actual` on Thursday and `AI Drafted` on Friday would be inviting exactly the
   * comparison mistake this page exists to prevent.
   *
   * RUN 81 — the TMS list is still empty when the stored draft arrives, and that needs nothing
   * here: `useLandOnDay` lands a list only once it has rows, so the TMS list lands on this same
   * day when `Load Appointments` brings them.
   */
  const landOn = landingDay(
    fixtures.map((fixture) => fixture.date),
    tournamentToday(tournament?.timezone ?? null, now),
  );

  /* ── The six derived tables, three per half ─────────────────────────────── */

  const actualMembers = useMemo(
    () => umpirePanelMembers(tmsFixtures, officials, "tmsActual"),
    [tmsFixtures, officials],
  );
  const aiMembers = useMemo(
    () => umpirePanelMembers(fixtures, officials, "aiReference"),
    [fixtures, officials],
  );

  const actualMatrix = useMemo(
    () =>
      buildUmpireMatrix(
        filterUmpireFixtures(tmsFixtures, tmsMatComp),
        actualMembers,
        "tmsActual",
        tmsMatOnField ? "onField" : "all",
      ),
    [tmsFixtures, actualMembers, tmsMatComp, tmsMatOnField],
  );
  const actualTeams = useMemo(
    () =>
      buildUmpireTeams(filterUmpireFixtures(tmsFixtures, tmsTcComp), actualMembers, "tmsActual"),
    [tmsFixtures, actualMembers, tmsTcComp],
  );
  const summaryRows = useMemo(
    () =>
      buildUmpireSummary(
        tmsFixtures,
        filterByGender(actualMembers, sumGender),
        "tmsActual",
        completedMatchIds,
      ),
    [tmsFixtures, actualMembers, sumGender, completedMatchIds],
  );

  const aiMatrix = useMemo(
    () =>
      buildUmpireMatrix(
        filterUmpireFixtures(fixtures, aiMatComp),
        aiMembers,
        "aiReference",
        aiMatOnField ? "onField" : "all",
      ),
    [fixtures, aiMembers, aiMatComp, aiMatOnField],
  );
  const aiTeams = useMemo(
    () => buildUmpireTeams(filterUmpireFixtures(fixtures, aiTcComp), aiMembers, "aiReference"),
    [fixtures, aiTcComp, aiMembers],
  );
  /* §B9's note compares the two halves over the SAME matches — the AI matrix panel's own
     filter — so the Umpires Managers' side is measured again under it rather than borrowed
     from the TMS panel, which the TD may have left on another tab. It reads duties alone, so
     `On Field Only` has nothing to change in it (§B6a). */
  const actualUnderAiFilter = useMemo(
    () =>
      buildUmpireMatrix(filterUmpireFixtures(tmsFixtures, aiMatComp), actualMembers, "tmsActual"),
    [tmsFixtures, actualMembers, aiMatComp],
  );

  /* RUN 81 — the AI half's cells join the change flash only once the stored record has been
     read. Arriving at the stored draft as the page opens is not a change (`useCellFlash`: "the
     first render never flashes"), and without this every held cell of the AI tables would light
     up on every visit. A key that was absent before is never a change, so from the first answer
     onwards the flash behaves exactly as it always has. */
  const storedRead = view !== null;
  const flashing = useCellFlash(
    useMemo(
      () =>
        new Map([
          ...umpireDerivedCellValues("tmsActual", actualMatrix, actualTeams, summaryRows),
          ...(storedRead ? umpireDerivedCellValues("aiReference", aiMatrix, aiTeams, []) : []),
        ]),
      [actualMatrix, actualTeams, summaryRows, aiMatrix, aiTeams, storedRead],
    ),
  );

  /* ── The writes ─────────────────────────────────────────────────────────── */

  /**
   * RUN 81 — read this system's own record as the page opens, and hold it without a stamp
   * (§Panel 5a).
   *
   * It is the same read `Load Appointments` makes, and the difference between the two is the
   * whole of §Panel 5a: this one shows the advisory half and nothing else, because the TMS half
   * answers to its own button, so it sets no stamp and writes no notice. It makes the page busy
   * while it runs — no press may go ahead over a draft nobody has seen — and a failure is kept in
   * `loadFailure`, where the AI list turns it into `Stored Draft Not Loaded` and offers
   * `Show Stored Draft`, which calls this again.
   */
  async function runLoad(retry = false): Promise<void> {
    // RUN 81B — nothing else may be running. `Load Appointments`, a draft, a clear or a cell edit
    // in flight would otherwise race this read, and whichever answered last would win the view.
    if (loadInFlight.current || pending !== null) return;
    loadInFlight.current = true;
    setPending({ kind: "load" });
    // RUN 81A — a failure is NOT cleared here. On a retry the failure stays on screen, and with it
    // the `Show Stored Draft` button the TD's focus is on, until the new read has an answer.
    try {
      const stored = await withinTime(writer.fetch({}), storedReadTimeoutMs);
      if (!mounted.current) return;
      setView(stored);
      setLoadFailure(null);
      // The retry button goes with the failure it answered, so the list's own title takes the
      // focus it held rather than leaving it on the page body — but only if the focus is still
      // there (RUN 81B): a TD who has moved on elsewhere is not pulled back.
      const focused = document.activeElement;
      if (retry && (focused === document.body || focused?.id === SHOW_STORED_DRAFT_ID)) {
        document.getElementById("aiFixTitle")?.focus();
      }
    } catch (error) {
      if (!mounted.current) return;
      setLoadFailure(
        error instanceof ApiError || error instanceof StoredReadTimeout
          ? error.message
          : "The stored draft could not be read. Nothing on this page has changed.",
      );
      // RUN 81B — counted, so the same refusal twice is announced twice (it re-keys the alert).
      setLoadAttempts((count) => count + 1);
    } finally {
      loadInFlight.current = false;
      // Only this read's own pending state is cleared, never another act's.
      if (mounted.current) setPending((current) => (current?.kind === "load" ? null : current));
    }
  }

  useEffect(() => {
    mounted.current = true;
    // A view handed in (a test's seed) is already the stored record; there is nothing to read.
    // `opened` keeps StrictMode's second mount effect from starting a second read (RUN 81A).
    if (initialView === null && !opened.current) {
      opened.current = true;
      void runLoad();
    }
    return () => {
      mounted.current = false;
    };
    // Once, as the panel opens. `runLoad` is rebuilt on every render and `initialView` is a seed
    // rather than a value to follow, so neither is a reason to read again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runFetch(): Promise<void> {
    setPending({ kind: "fetch" });
    setNote(null);
    setFailure(null);
    setPicker(null);
    try {
      const fetched = await writer.fetch({});
      setView(fetched);
      setFetchedAt(new Date());
      // The stored record is in hand now, whatever happened as the page opened.
      setLoadFailure(null);
      const count = fetched.tmsActual.violations;
      // Decision #13 (§B4a) — the notice says what the press did: it loaded this system's record.
      setNote(
        `Loaded · the umpire appointments recorded in this system · ` +
          (count === 0
            ? "no rule violations found."
            : `${String(count)} rule violation${count === 1 ? "" : "s"} flagged in red.`),
      );
    } catch (error) {
      setFailure(
        error instanceof ApiError
          ? error.message
          : "The appointments could not be loaded. Nothing on this page has changed.",
      );
    } finally {
      setPending(null);
    }
  }

  /**
   * RUN 38 — the advisory draft, in whichever of its two scopes was pressed.
   *
   * One function and not two, because the presses differ in exactly one value: how far they
   * reach. Everything else about them is identical and must stay identical — the same route,
   * the same `ai_reference` origin (a SQL literal in the repository, unreachable from here),
   * the same preservation of cells the TD has adjusted by hand, and the same standing truth
   * that nothing on this half is ever pushed anywhere. Two functions would be two places for
   * those to drift apart.
   *
   * The notice names the press, its coverage in days and matches, and the slots it wrote —
   * the shape the TO page's stage notice uses, so a Technical Delegate reading either page
   * reads the same sentence.
   */
  async function runDraft(draftScope: UmpireDraftScope): Promise<void> {
    setDraftPrompt(null);
    setPending({ kind: "draft", scope: draftScope });
    setNote(null);
    setFailure(null);
    setPicker(null);
    try {
      const drafted = await writer.draft({}, draftScope);
      setView(drafted);
      // RUN 81 — no stamp: it belongs to `Load Appointments` alone (§Panel 5a).
      setNote(umpireDraftNote(drafted));
      // RUN 82Q — the question that asked is gone, so the focus goes to what the press said: the
      // notice on a write that ran, the failure strip on one that did not. Asked for here rather
      // than done here, because neither is on the page until this render lands.
      setFocusRequest({ kind: "note" });
    } catch (error) {
      setFailure(
        error instanceof ApiError ? error.message : "The draft did not run. Nothing was written.",
      );
      setFocusRequest({ kind: "failure" });
    } finally {
      setPending(null);
    }
  }

  /**
   * RUN 40 — `Re-Draft`. The same advisory draft, shuffled.
   *
   * The Technical Delegate's first pool-stage draft on the TO side was even on every figure the
   * page measures and still paired the same two people four times over. Nothing about it was
   * wrong and there was nothing to fix; what he wanted was *another one*, equally legal,
   * arranged differently. RUN 38 gave the TO page that button and this half never had it, so a
   * TD comparing the two pages had one draft to hold the Umpires Managers up against on this
   * one and a choice of arrangements on the other.
   *
   * It is the same route, the same `ai_reference` origin and the same preservation rule as the
   * two draft presses beside it — the offset is the only thing that differs. So what it can
   * and cannot lose is exactly what they can and cannot lose: **cells the TD has adjusted by
   * hand are kept** (the API leaves any `td_modified` row untouched and does not even count it
   * among the slots it wrote), and **everything the engine drafted is redrawn**. `Clear Drafts`
   * is still the only control on this page that removes a hand edit, and it still says so.
   *
   * The variant is sent, then banked: `variant + 1` goes out and the counter follows on the way
   * back, so a press that fails leaves the counter where it was and the next press is the same
   * variant rather than a skipped one.
   */
  async function runReDraft(draftScope: UmpireDraftScope): Promise<void> {
    setRedraftPrompt(false);
    const next = variant + 1;
    setPending({ kind: "redraft" });
    setNote(null);
    setFailure(null);
    setPicker(null);
    try {
      const drafted = await writer.draft({}, draftScope, next);
      setView(drafted);
      setVariant(next);
      setNote(umpireDraftNote(drafted, next));
      setFocusRequest({ kind: "note" });
    } catch (error) {
      setFailure(
        error instanceof ApiError
          ? error.message
          : "The re-draft did not run. Nothing was written.",
      );
      setFocusRequest({ kind: "failure" });
    } finally {
      setPending(null);
    }
  }

  async function runClearDrafts(): Promise<void> {
    setClearPrompt(false);
    setPending({ kind: "clear" });
    setNote(null);
    setFailure(null);
    setPicker(null);
    try {
      const cleared = await writer.clearDrafts({});
      setView(cleared);
      setNote(
        `Clear Drafts · ${String(cleared.cleared)} advisory slot` +
          `${cleared.cleared === 1 ? "" : "s"} emptied. The Umpires Managers' appointments are ` +
          `untouched.`,
      );
      setFocusRequest({ kind: "note" });
    } catch (error) {
      setFailure(
        error instanceof ApiError
          ? error.message
          : "Nothing was cleared. The advisory draft is unchanged.",
      );
      setFocusRequest({ kind: "failure" });
    } finally {
      setPending(null);
    }
  }

  /** §B10's what-if edit — one advisory cell, filled or reassigned, and never pushed. */
  async function pickUmpire(
    fixture: UmpireFixtureBody,
    slot: UmpireSlotBody,
    tmsPersonId: string,
  ): Promise<void> {
    setPicker(null);
    if (view === null) return;

    const umpire = officials.find((entry) => entry.tmsPersonId === tmsPersonId);
    if (umpire === undefined) return;

    const row = umpireRowKey(fixture.tmsMatchDbId, slot.slot);
    const previous = view;
    setView(withOptimisticSlot(previous, fixture.tmsMatchDbId, slot.slot, umpire));
    setPending({ kind: "slot", row });
    setSlotError(null);
    setNote(null);

    try {
      const answered =
        slot.id === null
          ? await writer.create({
              tmsMatchDbId: fixture.tmsMatchDbId,
              slot: slot.slot,
              tmsPersonId,
            })
          : await writer.patch(slot.id, { tmsPersonId });
      setView(answered);
      setNote(
        `${fixture.displayMatchNo} ${slot.slot} · ${slot.mediaName ?? "Unfilled"} → ` +
          `${umpire.mediaName} · AI matrix, teams coverage and evenness note updated. ` +
          `Advisory only — nothing pushed.`,
      );
    } catch (error) {
      // The optimistic cell goes back first, and the three AI tables go back with it.
      setView(previous);
      setNote(null);
      setSlotError({
        row,
        message:
          error instanceof ApiError
            ? error.message
            : "The advisory cell was not changed. It has been put back.",
      });
    } finally {
      setPending(null);
    }
  }

  /**
   * §B11's two exports.
   *
   * **They are two documents and never one.** Part C invariant 5 keeps the halves apart on the
   * screen; a single workbook holding both would put the Umpires Managers' record and an
   * advisory draft in one file, where the first person to open the second tab would read the
   * engine's guesses as appointments. The advisory workbook says `Advisory` in its filename, its
   * sheet titles, its tab names and its note line for the same reason.
   *
   * Neither is a write, so neither makes the panel busy: a copy may be taken while a draft runs,
   * and what comes out is whatever the list in front of the TD is showing.
   */
  async function runExport(dataset: "tmsActual" | "aiReference"): Promise<void> {
    if (view === null) return;
    setExportingHalf(dataset);
    setFailure(null);
    try {
      // One instant for both the sheet and the filename — see the technical page's own note.
      const at = new Date();
      const tms = dataset === "tmsActual";
      const listed = tms ? tmsListed : aiListed;
      const comp = tms ? tmsFixComp : aiFixComp;
      const stage = tms ? tmsFixStage : aiFixStage;
      // The matrix SHEET is always the complete matrix — both competitions, the whole
      // schedule — whatever the panel's competition toggle shows, for the reason the TO
      // page's export gives: the toggles are a viewing convenience, and a shared half-matrix
      // that looks whole misleads its reader. The fixture sheet stays as-displayed.
      const exportMatrix = tms
        ? buildUmpireMatrix(tmsFixtures, actualMembers, "tmsActual")
        : buildUmpireMatrix(fixtures, aiMembers, "aiReference");
      const common = {
        tournament: tournament?.shortDisplayName ?? null,
        timezone: tournament?.timezone ?? null,
        at,
        fixtureScope: scopeLine(
          [
            comp === "all" ? null : competitionLabel(comp),
            stage === "all" ? null : stage === "pool" ? "Pool matches" : "Post-pool matches",
          ],
          listed.length,
        ),
        matrixScope: scopeLine(["both competitions", "whole schedule"], exportMatrix.fixtures),
      };
      const workbook = tms
        ? buildUmpireTmsWorkbook({
            ...common,
            fixtures: umpireTmsFixtureRows(listed),
            matrix: umpireMatrixRows(exportMatrix.rows),
          })
        : buildUmpireAiWorkbook({
            ...common,
            fixtures: umpireAiFixtureRows(listed),
            matrix: umpireMatrixRows(exportMatrix.rows),
          });
      const filename = exportFilename(workbook.base, at, tournament?.timezone ?? null);
      await exporter(workbook, filename);
      setNote(exportNotice(filename));
    } catch {
      setFailure("The workbook could not be written. Nothing has been downloaded.");
    } finally {
      setExportingHalf(null);
    }
  }

  const busy = pending !== null;
  const fetching = pending?.kind === "fetch";
  const draftingPool = pending?.kind === "draft" && pending.scope === "pool";
  const draftingAll = pending?.kind === "draft" && pending.scope === "all";
  const redrafting = pending?.kind === "redraft";
  const clearing = pending?.kind === "clear";

  const tmsListed = filterUmpireFixtures(tmsFixtures, tmsFixComp, tmsFixStage);
  const aiListed = filterUmpireFixtures(fixtures, aiFixComp, aiFixStage);
  const aiHasRows = fixtures.some((fixture) =>
    fixture.aiReference.some((slot) => slot.tmsPersonId !== null),
  );
  /**
   * RUN 81 — where the AI list's read of the stored record has got to (§Panel 5a): `ready` once a
   * view is in hand from any read or press, `failed` while the opening read's refusal stands, and
   * `loading` otherwise — which includes the first draw, before the read has begun.
   */
  const aiLoadState: "loading" | "failed" | "ready" =
    view !== null ? "ready" : loadFailure !== null ? "failed" : "loading";

  /**
   * RUN 82Q — a question is already on screen.
   *
   * All three are drawn in the one `.overridebox` frame, over a layer that takes the page's clicks
   * but does not dismiss anything — a stray click may not answer a decision. What that leaves is
   * the keyboard, and the finding this run fixes: the presses behind an open question were all
   * still reachable by Tab. Three layers now, each enough on its own in a browser — the page
   * behind a question is `inert`, so nothing on it can be pressed, tabbed to or read out; every
   * press that opens a question or the umpire popover returns while one is open; and every answer
   * is disabled while a write is out.
   */
  const questionOpen = draftPrompt !== null || redraftPrompt || clearPrompt;

  /**
   * RUN 82Q — open a question, unless one is already open, and remember the press that asked.
   *
   * It closes the umpire popover on the way. The popover is drawn outside the workbench, so
   * `inert` does not reach it, and a TD who tabbed from an open popover back to a draft button and
   * pressed Enter — no pointer, so its outside-click close never fired — would be left with a live
   * popover under the question, able to send a pick the question knew nothing about. It is the one
   * way a question is opened here, so no question added later can forget the rule.
   */
  function openQuestion(open: () => void): void {
    if (questionOpen) return;
    const pressed = document.activeElement;
    opener.current = pressed instanceof HTMLElement && pressed !== document.body ? pressed : null;
    setPicker(null);
    open();
  }

  /**
   * RUN 82Q — keyboard focus goes INTO whichever question opens, onto `Cancel`: the one answer
   * that changes nothing, so an Enter pressed in haste writes nothing.
   *
   * The page goes `inert` the moment a question opens, which takes the focus off the press that
   * opened it; without this it fell to the page body and a keyboard TD had to hunt for the
   * dialogue. Focus the TD has already put inside the question is left alone.
   */
  useEffect(() => {
    if (!questionOpen) return;
    const question = questionsRef.current?.querySelector<HTMLElement>('[role="alertdialog"]');
    if (question === null || question === undefined) return;
    const active = document.activeElement;
    if (active !== null && active !== document.body && question.contains(active)) return;
    const buttons = Array.from(question.querySelectorAll<HTMLButtonElement>("button"));
    const cancel = buttons.find((button) => button.textContent?.trim() === "Cancel");
    (cancel ?? buttons[0])?.focus();
  }, [questionOpen]);

  /**
   * RUN 82Q — Tab cannot leave an open question from OUTSIDE it either.
   *
   * `keepTabInside` sits on each question and so acts only while the focus is already within it. A
   * click on a question's own words puts the focus on the page body, and a Tab from there walked
   * out to How To Use and the module nav with the question still open. While one is open, a Tab
   * pressed anywhere else brings the focus into it — forwards to its first stop, backwards to its
   * last. Caught at the document before anything else hears it; inside the question it is left to
   * `keepTabInside`.
   */
  useEffect(() => {
    if (!questionOpen) return;
    function bringTabInside(event: KeyboardEvent): void {
      if (event.key !== "Tab") return;
      const question = questionsRef.current?.querySelector<HTMLElement>('[role="alertdialog"]');
      if (question === null || question === undefined) return;
      const active = document.activeElement;
      if (active !== null && question.contains(active)) return;
      const stops = questionTabStops(question);
      const target = event.shiftKey ? stops[stops.length - 1] : stops[0];
      if (target === undefined) return;
      event.preventDefault();
      target.focus();
    }
    document.addEventListener("keydown", bringTabInside, true);
    return () => {
      document.removeEventListener("keydown", bringTabInside, true);
    };
  }, [questionOpen]);

  /**
   * RUN 82Q — the hand-off once a question has closed: back to the press that asked on `Cancel`,
   * to the notice after a write that ran, to the failure strip after one that did not.
   *
   * Only where the focus has fallen to the page body, which is where it goes when the question
   * holding it closes. A draft can take seconds, and a TD who has tabbed on to something else
   * meanwhile is not pulled back; the notice is announced by its live region either way.
   */
  useEffect(() => {
    if (focusRequest === null) return;
    const active = document.activeElement;
    if (active !== null && active !== document.body) return;
    const press = opener.current;
    const back = press !== null && press.isConnected && !press.matches(":disabled") ? press : null;
    const target =
      focusRequest.kind === "note"
        ? (noteRef.current ?? failureRef.current)
        : focusRequest.kind === "failure"
          ? (failureRef.current ?? noteRef.current)
          : // `Cancel`: the press that asked, or — where it has gone, as `Re-Draft` does once the
            // draft it needs is cleared — whatever the page last said.
            (back ?? noteRef.current ?? failureRef.current);
    target?.focus();
  }, [focusRequest]);

  return (
    <>
      {/* ── §B2, the supervision banner ───────────────────────────────────── */}
      <div className="advisory" role="status" data-testid="supervision-banner">
        <span className="advisory__label">Supervision Only</span>
        <span className="advisory__msg">
          <b>Umpires Are Appointed By The Umpires Managers</b> — nothing on the TMS half of this
          page is editable or pushed to TMS. Your job here is oversight: check the appointments
          break no rules, and that opportunities are spread fairly.
        </span>
        <span className="advisory__fetch" data-testid="fetch-stamp">
          {fetchStampText(fetchedAt)}
        </span>
      </div>

      {view?.tdHandlesUmpireAppointments === true ? (
        <div className="warnnote warnnote--advisory" role="status" data-testid="ownership-note">
          <span className="warnnote__kind">Ownership</span>
          <span className="warnnote__msg">
            Configuration §06 has <b>TD Also Handles Umpire Appointments</b> switched on. This
            screen still supervises rather than appoints: the TMS half stays read-only and the
            engine&rsquo;s draft stays advisory until that path is built.
          </span>
        </div>
      ) : null}

      {/* RUN 82Q — both take the focus after a question's answer, so both can hold it without
          becoming a stop in the page's tab order. They are drawn above everything the question
          makes `inert`, so what a write says is still read out while the next question is open. */}
      {note === null ? null : (
        <p className="apptnote" role="status" data-testid="action-note" tabIndex={-1} ref={noteRef}>
          {note}
        </p>
      )}
      {failure === null ? null : (
        <div
          className="writeerror"
          role="alert"
          data-testid="action-failure"
          tabIndex={-1}
          ref={failureRef}
        >
          <b>Not Done</b>
          {failure}
        </div>
      )}

      {/* ── SECTION 1 — TMS ACTUAL ────────────────────────────────────────── */}
      <div className="tmsband">
        {/* Decision #13 (§B4a) — this system's record, and nothing on this page reads TMS. */}
        <h2>
          Umpire Appointments <span className="md">· As Recorded In This System</span>
        </h2>
        <span className="cmpchip" data-testid="compliance-chip">
          {fetchedAt === null || view === null ? (
            <span>Not Loaded This Session</span>
          ) : view.tmsActual.violations === 0 ? (
            <>
              <span className="good">
                <b>0</b> Rule Violations
              </span>
              <span className="sep" aria-hidden="true">
                ·
              </span>
              <span>
                All <b>{view.counts.fixtures}</b> Matches Pass
              </span>
            </>
          ) : (
            <>
              <span className="bad">
                <b>{view.tmsActual.violations}</b> Rule{" "}
                {view.tmsActual.violations === 1 ? "Violation" : "Violations"}
              </span>
              <span className="sep" aria-hidden="true">
                ·
              </span>
              <span>
                Across <b>{view.tmsActual.matchesWithViolations}</b> Of{" "}
                <b>{view.counts.fixtures}</b> Matches
              </span>
            </>
          )}
        </span>
      </div>

      {/* RUN 82Q — `inert` while any question is open, so the page behind it can be neither
          pressed, tabbed to nor read out: the modality `aria-modal` promises and only the browser
          can keep. It is marked on each block that holds a control rather than on one wrapper,
          because this page lays itself out as sibling grids — two workbenches and the coverage
          pair — and a wrapper around them would need a rule of its own in the stylesheet to stay
          out of the layout. The banner, the bands, the notice and the questions are all outside
          them, so none of them is inside what goes inert. */}
      {fetching && fetchedAt === null ? (
        <section className="panel panel--d1" data-testid="umpire-loading" inert={questionOpen}>
          <div className="panel__head">
            <h3 className="panel__title">Fixture List</h3>
            <span className="panel__note">Loading</span>
          </div>
          <Skeleton rows={8} />
        </section>
      ) : (
        <div className="workbench" data-testid="tms-workbench" inert={questionOpen}>
          <UmpireFixtureListPanel
            dataset="tmsActual"
            fixtures={tmsListed}
            allFixtures={tmsFixtures}
            competition={tmsFixComp}
            onCompetition={setTmsFixComp}
            stage={tmsFixStage}
            onStage={setTmsFixStage}
            busy={busy}
            onFetch={() => void runFetch()}
            fetching={fetching}
            opening={pending?.kind === "load"}
            landOn={landOn}
            onExport={() => void runExport("tmsActual")}
            exporting={exportingHalf === "tmsActual"}
            /* Before the press there is no record on this half to export, and a file named `TMS
               Actual` holding nothing would be the worst thing this page could produce. */
            exportable={fetchedAt !== null}
          />

          <UmpireMatrixPanel
            dataset="tmsActual"
            table={actualMatrix}
            competition={tmsMatComp}
            onCompetition={setTmsMatComp}
            onFieldOnly={tmsMatOnField}
            onOnFieldOnly={setTmsMatOnField}
            flashing={flashing}
          />
        </div>
      )}

      <div className="covpair" data-testid="tms-covpair" inert={questionOpen}>
        <UmpireTeamsPanel
          dataset="tmsActual"
          table={actualTeams}
          competition={tmsTcComp}
          onCompetition={setTmsTcComp}
          flashing={flashing}
        />
        <UmpireSummaryPanel
          rows={summaryRows}
          gender={sumGender}
          onGender={setSumGender}
          flashing={flashing}
          scheduleLoaded={scheduleLoaded}
        />
      </div>

      {/* ── SECTION 2 — AI REFERENCE ──────────────────────────────────────── */}
      <div className="aiband" data-testid="ai-band">
        <span className="aichip">
          <span aria-hidden="true">◆</span>AI Reference
        </span>
        <h2>AI Drafted Appointments</h2>
        <p>
          Not a target and never pushed anywhere — this is the engine&rsquo;s evenly-balanced
          draft, shown purely so you can judge how evenly the Umpires Managers have spread
          opportunities above. Orange title bars mark everything AI-drafted.
        </p>
      </div>

      <div className="workbench" data-testid="ai-workbench" inert={questionOpen}>
        <UmpireFixtureListPanel
          dataset="aiReference"
          fixtures={aiListed}
          allFixtures={fixtures}
          competition={aiFixComp}
          onCompetition={setAiFixComp}
          stage={aiFixStage}
          onStage={setAiFixStage}
          /* RUN 81 — every write on this half waits for the stored record (§Panel 5a): until it
             has been read there may be a draft on it that nobody has seen. */
          busy={busy || view === null}
          loadState={aiLoadState}
          loadFailure={loadFailure}
          onShowStoredDraft={() => void runLoad(true)}
          /* RUN 81B — asleep while ANY read or write is out, not only its own. */
          loadingAgain={loadFailure !== null && pending !== null}
          loadAttempts={loadAttempts}
          {...(view === null ? {} : { storedDraft: aiHasRows })}
          /* RUN 82Q — every press that opens a question goes through `openQuestion`, so none of
             them can open a second one over one already on screen. */
          onDraftPool={() => {
            openQuestion(() => {
              setDraftPrompt("pool");
            });
          }}
          draftingPool={draftingPool}
          onDraftAll={() => {
            openQuestion(() => {
              setDraftPrompt("all");
            });
          }}
          draftingAll={draftingAll}
          onReDraft={() => {
            openQuestion(() => {
              setRedraftPrompt(true);
            });
          }}
          redrafting={redrafting}
          onClearDrafts={() => {
            openQuestion(() => {
              setClearPrompt(true);
            });
          }}
          clearing={clearing}
          openSlot={
            picker === null
              ? null
              : { tmsMatchDbId: picker.fixture.tmsMatchDbId, slot: picker.slot.slot }
          }
          onOpenPicker={(fixture, slot, anchor) => {
            // RUN 82Q — the popover is a way into a write, so it does not open under a question.
            if (questionOpen) return;
            setPicker((current) =>
              current !== null &&
              current.fixture.tmsMatchDbId === fixture.tmsMatchDbId &&
              current.slot.slot === slot.slot
                ? null
                : { fixture, slot, anchor },
            );
          }}
          savingRow={pending?.kind === "slot" ? pending.row : null}
          slotError={slotError}
          landOn={landOn}
          onExport={() => void runExport("aiReference")}
          exporting={exportingHalf === "aiReference"}
          /* Nothing drafted is nothing to compare against; an empty advisory workbook would
             read to the Umpires Managers as a draft that appoints nobody. */
          exportable={aiHasRows}
        />

        <UmpireMatrixPanel
          dataset="aiReference"
          table={aiMatrix}
          competition={aiMatComp}
          onCompetition={setAiMatComp}
          onFieldOnly={aiMatOnField}
          onOnFieldOnly={setAiMatOnField}
          flashing={flashing}
        >
          {/* RUN 81 — until `Load Appointments` is pressed the Umpires Managers' side is not on
              this page, and the note says so rather than measuring an empty half. */}
          <EvennessNote
            actual={fetchedAt === null ? null : actualUnderAiFilter}
            reference={aiMatrix}
          />
        </UmpireMatrixPanel>

        <UmpireTeamsPanel
          dataset="aiReference"
          table={aiTeams}
          competition={aiTcComp}
          onCompetition={setAiTcComp}
          flashing={flashing}
        />
      </div>

      {picker === null ? null : (
        <UmpirePicker
          fixture={picker.fixture}
          slot={picker.slot.slot}
          officials={officials}
          allFixtures={fixtures}
          anchor={picker.anchor}
          onPick={(tmsPersonId) => {
            void pickUmpire(picker.fixture, picker.slot, tmsPersonId);
          }}
          onClose={() => {
            setPicker(null);
            picker.anchor.focus();
          }}
        />
      )}

      {/* RUN 82Q (final QA, 18 September 2026) — every question this panel draws, in one place the
          focus can be looked for without finding anybody else's dialogue. A plain block: each
          question inside it is fixed to the viewport, so it lays nothing out, and it sits outside
          everything `inert` reaches.

          All three are modal here the way the TO page's are: `aria-modal`, their own body as their
          description, the focus arriving on `Cancel` and Tab kept inside (`keepTabInside`). */}
      <div ref={questionsRef} data-testid="umpire-questions">
        {/* RUN 82Q — the shadow the frame draws takes no clicks, so the masthead, How To Use and
            the module nav behind it all still answered one while a question was open. This
            transparent layer, just under the question, takes those clicks and does nothing with
            them — no dismiss, as these questions have never had one. The class is the TO page's
            own, already in the stylesheet. */}
        {questionOpen ? (
          <div className="questionscrim" aria-hidden="true" data-testid="question-scrim" />
        ) : null}
        {draftPrompt === null ? null : (
          /* Two buttons that both say "draft" need a sentence apiece saying WHICH matches, and
             that is most of what this box is. The rest is the standing advisory note, said before
             the press rather than only after it: nothing on this half is a target, nothing is
             ever pushed, and the Umpires Managers own the real appointments (§B1, §B9). */
          <div
            className="overridebox"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="umpDraftTitle"
            /* RUN 82Q — the body is the question's description, and the stored-draft sentence
               joins it only on the renders that draw it. */
            aria-describedby={describedBy([
              "umpDraftCopy",
              view !== null && aiHasRows ? "umpDraftStored" : null,
              "umpDraftKeeps",
              "umpDraftAdvisory",
            ])}
            data-testid="draft-prompt"
            onKeyDown={keepTabInside}
          >
            <b id="umpDraftTitle">{UMPIRE_DRAFT_LABEL[draftPrompt]}</b>
            <p id="umpDraftCopy" data-testid="draft-prompt-copy">
              {draftPrompt === "all"
                ? "Drafts U1, U2, RU and VU for EVERY match in the current schedule — the pool stage and everything after it."
                : "Drafts U1, U2, RU and VU for the POOL MATCHES only. Anything after the pool stage is left blank."}{" "}
              Both competitions, respecting the country rule and the one on-field duty per day,
              and spreading duties as evenly as the schedule allows.
            </p>
            {/* RUN 81 — §Panel 5a. Said before the press, because this press replaces a draft the
                TD may have spent an evening checking. */}
            {view !== null && aiHasRows ? (
              <p id="umpDraftStored" data-testid="draft-prompt-stored">
                <b>A stored draft is already on this half</b> &mdash;{" "}
                {countOf(view.aiReference.filled, "advisory slot")}. Running this draft replaces
                the engine-drafted cells it reaches.
              </p>
            ) : null}
            <p id="umpDraftKeeps" data-testid="draft-prompt-keeps">
              Advisory cells you have <b>adjusted by hand are kept</b>. Use{" "}
              <b>Clear Drafts</b> if you want the reference half emptied instead.
            </p>
            <p id="umpDraftAdvisory" data-testid="draft-prompt-advisory">
              This is a <b>reference only</b>. Nothing here is pushed anywhere, and the Umpires
              Managers&rsquo; appointments above are not touched and could not be.
            </p>
            {/* RUN 82Q — this answer, and the answer of each question below, is disabled while any
                write is out: an answer given then would start a second write beside the first. */}
            <div className="overridebox__acts">
              <button
                type="button"
                className="gbtn gbtn--solid"
                disabled={busy}
                onClick={() => void runDraft(draftPrompt)}
              >
                Run The Draft
              </button>
              <button
                type="button"
                className="gbtn"
                onClick={() => {
                  setDraftPrompt(null);
                  // RUN 82Q — the answer that changes nothing hands the focus back to the press
                  // that asked, so a keyboard TD is returned exactly where he left off.
                  setFocusRequest({ kind: "opener" });
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {!redraftPrompt ? null : (
          /* The draft modal's idiom, with the copy the shuffle needs: what it produces (a
             different but equally even draft), how far it reaches (asked here, because one
             button cannot be two scopes) and — the sentence that makes it safe to press — what
             it keeps and what it redraws. Both halves of that last one are stated, because "your
             edits are kept" alone would leave a TD wondering what happened to the rest. */
          <div
            className="overridebox"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="umpRedraftTitle"
            aria-describedby="umpRedraftCopy umpRedraftKeeps umpRedraftAdvisory umpRedraftAgain"
            data-testid="redraft-prompt"
            onKeyDown={keepTabInside}
          >
            <b id="umpRedraftTitle">Re-Draft — Variant {variant + 1}</b>
            {/* RUN 82Q — the scope is the question's first tab stop, at whichever radio is
                checked, so Shift+Tab from `Cancel` reaches the choice rather than skipping it. */}
            <fieldset className="clearscope" data-testid="redraft-scope">
              <legend className="kicker">How far it reaches</legend>
              {(["pool", "all"] as UmpireDraftScope[]).map((choice) => (
                <label key={choice}>
                  <input
                    type="radio"
                    name="umpire-redraft-scope"
                    value={choice}
                    checked={redraftScope === choice}
                    onChange={() => {
                      setRedraftScope(choice);
                    }}
                  />
                  {choice === "pool" ? "The Pool Stage" : "The Whole Schedule"}
                </label>
              ))}
            </fieldset>
            <p id="umpRedraftCopy" data-testid="redraft-prompt-copy">
              Drafts {redraftScope === "all" ? "every match in the schedule" : "the pool matches"}{" "}
              again and produces a <b>different</b> arrangement &mdash; not a better one. It
              obeys every rule this draft obeys and spreads duties as evenly; the umpires are
              simply paired and spread another way.
            </p>
            <p id="umpRedraftKeeps" data-testid="redraft-prompt-keeps">
              <b>Advisory cells you have adjusted by hand are kept.</b> Everything the engine
              drafted is redrawn. Use <b>Clear Drafts</b> if you want the reference half emptied
              instead &mdash; that is the only control here that removes your own edits.
            </p>
            <p id="umpRedraftAdvisory" data-testid="redraft-prompt-advisory">
              Still a <b>reference only</b>. Nothing here is pushed anywhere, and the Umpires
              Managers&rsquo; appointments above are not touched and could not be.
            </p>
            <p id="umpRedraftAgain">
              Press it again for another variant. Each one is repeatable rather than random, so
              nothing here is a gamble &mdash; but there is no way back to a variant you have
              drafted over, so stop on the one you want.
            </p>
            <div className="overridebox__acts">
              <button
                type="button"
                className="gbtn gbtn--solid"
                disabled={busy}
                onClick={() => void runReDraft(redraftScope)}
              >
                Run The Re-Draft
              </button>
              <button
                type="button"
                className="gbtn"
                onClick={() => {
                  setRedraftPrompt(false);
                  setFocusRequest({ kind: "opener" });
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {!clearPrompt ? null : (
          <div
            className="overridebox"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="umpClearTitle"
            aria-describedby="umpClearCopy"
            data-testid="clear-prompt"
            onKeyDown={keepTabInside}
          >
            <b id="umpClearTitle">Clear The Advisory Draft</b>
            <p id="umpClearCopy">
              Empties every AI-drafted cell on the reference half, <b>including the ones you have
              adjusted by hand</b>. The Umpires Managers&rsquo; appointments above are not touched
              and could not be — nothing on this page can reach them.
            </p>
            <div className="overridebox__acts">
              <button
                type="button"
                className="gbtn gbtn--solid"
                disabled={busy}
                onClick={() => void runClearDrafts()}
              >
                Clear Them
              </button>
              <button
                type="button"
                className="gbtn"
                onClick={() => {
                  setClearPrompt(false);
                  setFocusRequest({ kind: "opener" });
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
